import { useMemo } from 'react'

import type { DateRange } from 'react-day-picker'

import { getFormattedDateRange } from '@/components/ui/export.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useCreators } from '@/modules/creators/hooks/useCreators.ts'
import { getChatterTableSheetData } from '@/modules/tracking/chatter-tracking/components/all-chatters-table/export.ts'
import type { AllChatterTableType } from '@/modules/tracking/chatter-tracking/service/chatter-tracking-service.ts'

import { getLeaderboardChattersColumns } from '../../../tracking/chatter-tracking/components/all-chatters-table/columns.tsx'
import { useChatterOverviewSelect } from '../../hook/useChatterOverviewSelect.ts'
import { ChatterCombobox } from '../chatter-combobox.tsx'

interface ChatterLeaderboardTableProps {
  chatters: AllChatterTableType[]
  isFetching: boolean
  date: DateRange
}

export const ChatterLeaderboardTable = ({
  date,
  chatters,
  isFetching,
}: ChatterLeaderboardTableProps) => {
  const {
    selection: { defaultSelectedCreators },
  } = useCreators()
  const { userSettings } = useAccount()
  const { selectedChatters, allSelect, allRemove, addSelect, removeSelect } =
    useChatterOverviewSelect(chatters)

  const exportOptions = useMemo(
    () => ({
      fileName: `Chatter Leaderboard, ${getFormattedDateRange(date)}`,
      getTableSheetData: () => getChatterTableSheetData(selectedChatters),
    }),
    [date, selectedChatters],
  )

  const tableExtra = useMemo(
    () => (
      <ChatterCombobox
        chatters={chatters}
        select={selectedChatters}
        onAllSelect={allSelect}
        onAllRemove={allRemove}
        onAddSelect={addSelect}
        onRemoveSelect={removeSelect}
      />
    ),
    [chatters, selectedChatters, allSelect, allRemove, addSelect, removeSelect],
  )

  const columns = useMemo(
    () => getLeaderboardChattersColumns(userSettings?.showCents),
    [userSettings?.showCents],
  )

  if (isFetching || !defaultSelectedCreators) {
    return <Skeleton className="h-40 w-full" />
  }

  return (
    <DataTable
      title="Chatter Leaderboard"
      enableSorting
      enablePagination
      enableExport
      exportOptions={exportOptions}
      columns={columns}
      data={selectedChatters}
      extra={tableExtra}
      testId="chatter-leaderboard"
    />
  )
}
